import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search, MapPin, Languages, Users, ChevronRight, ChevronDown, Filter, X,
  SlidersHorizontal, MapPinned, Briefcase, Mic, Globe, Handshake, MessageCircle, RotateCcw, Tag,
} from 'lucide-react';
import { api, absoluteUrl } from '../lib/api';
import { fuzzyContains } from '../lib/fuzzySearch';
import Highlight from '../components/Highlight';
import PageHero from '../components/PageHero';
import { useTranslation } from '../i18nContext';

const ALPHABET = ['#', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

function firstLetter(name) {
  const n = (name || '').trim();
  if (!n) return '#';
  // Normalize accents (Á→A, Ñ→N)
  const c = n.normalize('NFD').replace(/[\u0300-\u036f]/g, '').charAt(0).toUpperCase();
  return /[A-Z]/.test(c) ? c : '#';
}

/** Returns the surname's first letter for an interpreter.
 *  Prefers the new `apellido_paterno` field (post-migration). Falls back to
 *  `surname_sort_key` (set by the roster sync) and, ultimately, a heuristic
 *  on `full_name`. */
function surnameLetter(it) {
  const ap = (it.apellido_paterno || '').trim();
  if (ap) return firstLetter(ap);
  const key = (it.surname_sort_key || '').trim();
  if (key) return firstLetter(key);
  const parts = (it.full_name || '').split(/\s+/).filter(Boolean);
  for (let i = 1; i < parts.length; i += 1) {
    const tok = parts[i].toLowerCase();
    if (!['de', 'la', 'los', 'las', 'del', 'da', 'do', 'el', 'y'].includes(tok)) {
      return firstLetter(parts[i]);
    }
  }
  return firstLetter(parts[parts.length - 1] || it.full_name);
}

/** Build the display name from the new fields (paterno, materno, nombres).
 *  Falls back to full_name when fields are missing. The display order is
 *  "Apellido Paterno Apellido Materno, Nombres" — easier to scan in a
 *  directory listing where the surname is the primary key. */
function displayName(it) {
  const ap = (it.apellido_paterno || '').trim();
  const am = (it.apellido_materno || '').trim();
  const nom = (it.nombres || '').trim();
  if (ap || am || nom) {
    const apellidos = [ap, am].filter(Boolean).join(' ');
    return apellidos && nom ? `${apellidos}, ${nom}` : (apellidos || nom);
  }
  return it.full_name || '';
}

function cityFromText(c) {
  if (!c) return null;
  // Take first comma-separated piece, e.g. "Cancún, Quintana Roo, México" → "Cancún"
  return c.split(',')[0].trim();
}

// Robustly read `specialty` regardless of legacy schema:
//   • new schema: list[str] (one item per atom)
//   • old schema: comma-separated string
//   • either-missing: empty list
// Always returns string[] of trimmed non-empty atoms.
function readSpecialties(it) {
  if (Array.isArray(it.specialties_list) && it.specialties_list.length) {
    return it.specialties_list.map((s) => String(s).trim()).filter(Boolean);
  }
  const raw = it.specialty;
  if (Array.isArray(raw)) {
    return raw.map((s) => String(s).trim()).filter(Boolean);
  }
  if (typeof raw === 'string' && raw) {
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

function countryFromText(c) {
  if (!c) return null;
  const parts = c.split(',').map((s) => s.trim()).filter(Boolean);
  if (!parts.length) return null;
  // Default: last segment is the country
  let candidate = parts[parts.length - 1];
  // Common Spanish-language country tokens — normalize and return only if it's truly a country.
  const COUNTRIES = new Set([
    'México', 'Mexico', 'Estados Unidos', 'United States', 'USA', 'EE.UU.',
    'España', 'Spain', 'Francia', 'France', 'Italia', 'Italy',
    'Brasil', 'Brazil', 'Argentina', 'Colombia', 'Chile', 'Perú',
    'Canadá', 'Canada', 'Reino Unido', 'UK', 'Turquía', 'Turkey',
    'Alemania', 'Germany', 'Portugal', 'Egipto', 'Egypt',
  ]);
  if (COUNTRIES.has(candidate)) return candidate;
  // Otherwise, try second-to-last; if not found, return null (avoid mislabeling)
  if (parts.length >= 2 && COUNTRIES.has(parts[parts.length - 2])) return parts[parts.length - 2];
  return null;
}

// Map modality canonical name (or close variant) → compact icon + short label.
// Unknown values fall back to a generic Tag icon with the full value as label
// (truncated visually in the card chip).
const MODALITY_META = {
  'Simultánea presencial':                    { icon: Mic,            short: 'Presencial' },
  'Simultanea presencial':                    { icon: Mic,            short: 'Presencial' },
  'Remota (RSI)':                             { icon: Globe,          short: 'RSI' },
  'Remota':                                   { icon: Globe,          short: 'RSI' },
  'RSI':                                      { icon: Globe,          short: 'RSI' },
  'Interpretación de enlace / acompañamiento':{ icon: Handshake,      short: 'Enlace' },
  'Interpretacion de enlace / acompanamiento':{ icon: Handshake,      short: 'Enlace' },
  'Enlace':                                   { icon: Handshake,      short: 'Enlace' },
  'Acompañamiento':                           { icon: Handshake,      short: 'Enlace' },
  'Susurrada':                                { icon: MessageCircle,  short: 'Susurro' },
  'Consecutiva':                              { icon: RotateCcw,      short: 'Consec.' },
};

export function modalityChipMeta(m) {
  if (!m) return null;
  const exact = MODALITY_META[m];
  if (exact) return { ...exact, full: m };
  // Case-insensitive lookup fallback
  const lc = String(m).toLowerCase();
  for (const k of Object.keys(MODALITY_META)) {
    if (k.toLowerCase() === lc) return { ...MODALITY_META[k], full: m };
  }
  // Unknown / custom modality → generic icon, truncated label
  return { icon: Tag, short: m, full: m };
}


export default function DirectoryPage() {
  const t = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [all, setAll] = useState([]);
  // Initial state read from URL so shared links restore filters instantly.
  const readList = (k) => (searchParams.get(k) || '').split(',').map((s) => s.trim()).filter(Boolean);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [loading, setLoading] = useState(true);
  const [letter, setLetter] = useState((searchParams.get('letra') || 'TODOS').toUpperCase());
  const [filtersOpen, setFiltersOpen] = useState(false);  // Hidden by default — user must click "Filtros" to open
  // Multi-select filters
  const [selected, setSelected] = useState({
    cities: readList('ciudad'),
    countries: readList('pais'),
    languages: readList('idioma'),
    specialties: readList('especialidad'),
    modalities: readList('modalidad'),
  });

  // Sync state → URL (debounced via React batching, no extra timers needed).
  useEffect(() => {
    const next = new URLSearchParams();
    if (search.trim()) next.set('q', search.trim());
    if (letter && letter !== 'TODOS') next.set('letra', letter);
    if (selected.countries.length) next.set('pais', selected.countries.join(','));
    if (selected.cities.length) next.set('ciudad', selected.cities.join(','));
    if (selected.languages.length) next.set('idioma', selected.languages.join(','));
    if (selected.specialties.length) next.set('especialidad', selected.specialties.join(','));
    if (selected.modalities.length) next.set('modalidad', selected.modalities.join(','));
    setSearchParams(next, { replace: true });
  }, [search, letter, selected, setSearchParams]);

  // Load everything ONCE (we filter client-side for instant UX on 88 entries).
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/interpreters')
      .then(({ data }) => { if (alive) setAll(data || []); })
      .catch(() => { if (alive) setAll([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Build facet counts from the FULL dataset (so users see all options even after a filter).
  const facets = useMemo(() => {
    const cities = new Map();
    const countries = new Map();
    const languages = new Map();
    const specialties = new Map();
    const modalities = new Map();
    for (const i of all) {
      const city = cityFromText(i.city);
      if (city) cities.set(city, (cities.get(city) || 0) + 1);
      const country = countryFromText(i.city);
      if (country) countries.set(country, (countries.get(country) || 0) + 1);
      const langs = Array.from(new Set([...(i.active_languages || []), ...(i.passive_languages || [])]));
      for (const l of langs) languages.set(l, (languages.get(l) || 0) + 1);
      const specs = readSpecialties(i);
      for (const s of specs) specialties.set(s, (specialties.get(s) || 0) + 1);
      const mods = Array.isArray(i.modalities) ? i.modalities.filter(Boolean) : [];
      for (const m of mods) modalities.set(m, (modalities.get(m) || 0) + 1);
    }
    const sortByCount = (m) => Array.from(m.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'es'));
    return {
      cities: sortByCount(cities),
      countries: sortByCount(countries),
      languages: sortByCount(languages),
      specialties: sortByCount(specialties),
      modalities: sortByCount(modalities),
    };
  }, [all]);

  // Filter pipeline: text search → letter → facets
  const filtered = useMemo(() => {
    const q = search.trim();
    return all.filter((it) => {
      if (q) {
        const langsList = [
          ...(it.active_languages || []),
          ...(it.passive_languages || []),
        ].join(' ');
        const specsList = readSpecialties(it).join(' ');
        const hay = `${it.full_name || ''} ${it.nombres || ''} ${it.apellido_paterno || ''} ${it.apellido_materno || ''} ${it.city || ''} ${it.languages || ''} ${langsList} ${specsList}`;
        if (!fuzzyContains(hay, q)) return false;
      }
      if (letter !== 'TODOS') {
        if (surnameLetter(it) !== letter) return false;
      }
      if (selected.cities.length && !selected.cities.includes(cityFromText(it.city))) return false;
      if (selected.countries.length && !selected.countries.includes(countryFromText(it.city))) return false;
      if (selected.languages.length) {
        const langs = new Set([...(it.active_languages || []), ...(it.passive_languages || [])]);
        if (!selected.languages.some((l) => langs.has(l))) return false;
      }
      if (selected.specialties.length) {
        const specs = new Set(readSpecialties(it));
        if (!selected.specialties.some((s) => specs.has(s))) return false;
      }
      if (selected.modalities.length) {
        const mods = new Set(Array.isArray(it.modalities) ? it.modalities : []);
        if (!selected.modalities.some((m) => mods.has(m))) return false;
      }
      return true;
    });
  }, [all, search, letter, selected]);

  // Which letters actually have results AFTER text+facet filter (excluding letter filter itself)
  const availableLetters = useMemo(() => {
    const set = new Set();
    const q = search.trim();
    for (const it of all) {
      if (q) {
        const langsList = [
          ...(it.active_languages || []),
          ...(it.passive_languages || []),
        ].join(' ');
        const specsList = readSpecialties(it).join(' ');
        const hay = `${it.full_name || ''} ${it.nombres || ''} ${it.apellido_paterno || ''} ${it.apellido_materno || ''} ${it.city || ''} ${it.languages || ''} ${langsList} ${specsList}`;
        if (!fuzzyContains(hay, q)) continue;
      }
      if (selected.cities.length && !selected.cities.includes(cityFromText(it.city))) continue;
      if (selected.countries.length && !selected.countries.includes(countryFromText(it.city))) continue;
      if (selected.languages.length) {
        const langs = new Set([...(it.active_languages || []), ...(it.passive_languages || [])]);
        if (!selected.languages.some((l) => langs.has(l))) continue;
      }
      if (selected.specialties.length) {
        const specs = new Set(readSpecialties(it));
        if (!selected.specialties.some((s) => specs.has(s))) continue;
      }
      if (selected.modalities.length) {
        const mods = new Set(Array.isArray(it.modalities) ? it.modalities : []);
        if (!selected.modalities.some((m) => mods.has(m))) continue;
      }
      set.add(surnameLetter(it));
    }
    return set;
  }, [all, search, selected]);

  // Group filtered results by letter for the index
  const groupedByLetter = useMemo(() => {
    const g = new Map();
    for (const it of filtered) {
      const l = surnameLetter(it);
      if (!g.has(l)) g.set(l, []);
      g.get(l).push(it);
    }
    return g;
  }, [filtered]);

  function toggleFacet(group, value) {
    setSelected((s) => {
      const arr = s[group];
      return { ...s, [group]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });
  }

  function clearGroup(group) {
    setSelected((s) => ({ ...s, [group]: [] }));
  }

  function clearAll() {
    setSelected({ cities: [], countries: [], languages: [], specialties: [], modalities: [] });
    setSearch('');
    setLetter('TODOS');
  }

  const activeFilterCount =
    selected.cities.length + selected.countries.length + selected.languages.length + selected.specialties.length + selected.modalities.length
    + (letter !== 'TODOS' ? 1 : 0)
    + (search.trim() ? 1 : 0);

  const profileBase = `/${t.locale}/${t.routes.directory}`;

  return (
    <>
      <PageHero label={t.directory.label} title={t.directory.title} subtitle={t.directory.subtitle} />

      <section className="py-10 bg-gray-50 min-h-screen" data-testid="directory-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top bar: search + horizontal multi-select filter dropdowns */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[260px]">
                <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-charcoal-400" />
                <input
                  type="text"
                  data-testid="directory-search-input"
                  placeholder={t.directory.searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500 transition-all text-charcoal-700 placeholder:text-charcoal-400 text-sm"
                />
              </div>
              {/* Mobile: open drawer */}
              <button
                onClick={() => setFiltersOpen(true)}
                data-testid="directory-open-filters-btn"
                className="md:hidden relative inline-flex items-center gap-2 bg-cmic-500 text-white px-5 py-3 rounded-xl text-sm font-semibold hover:bg-cmic-600 transition-colors"
              >
                <SlidersHorizontal size={16} /> Filtros
                {activeFilterCount > 0 && (
                  <span className="bg-white text-cmic-700 text-[11px] font-bold rounded-full min-w-[20px] h-[20px] flex items-center justify-center px-1.5">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <span className="text-xs text-charcoal-500 ml-auto whitespace-nowrap" data-testid="directory-count">
                <span className="font-semibold text-charcoal-800">{filtered.length}</span> de {all.length}
              </span>
            </div>

            {/* Horizontal dropdown bar (md+) */}
            <div className="hidden md:flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100">
              <DirectoryFilterDropdown label="País" icon={MapPinned} items={facets.countries} selected={selected.countries} onToggle={(v) => toggleFacet('countries', v)} onClear={() => clearGroup('countries')} testid="directory-dd-countries" />
              <DirectoryFilterDropdown label="Ciudad" icon={MapPin} items={facets.cities} selected={selected.cities} onToggle={(v) => toggleFacet('cities', v)} onClear={() => clearGroup('cities')} testid="directory-dd-cities" />
              <DirectoryFilterDropdown label="Idioma" icon={Languages} items={facets.languages} selected={selected.languages} onToggle={(v) => toggleFacet('languages', v)} onClear={() => clearGroup('languages')} testid="directory-dd-languages" />
              <DirectoryFilterDropdown label="Modalidad" icon={Mic} items={facets.modalities} selected={selected.modalities} onToggle={(v) => toggleFacet('modalities', v)} onClear={() => clearGroup('modalities')} testid="directory-dd-modalities" />
              <DirectoryFilterDropdown label="Especialidad" icon={Briefcase} items={facets.specialties} selected={selected.specialties} onToggle={(v) => toggleFacet('specialties', v)} onClear={() => clearGroup('specialties')} testid="directory-dd-specialties" />
              {activeFilterCount > 0 && (
                <button
                  onClick={clearAll}
                  data-testid="directory-clear-filters-btn"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-charcoal-600 hover:text-rose-600 px-3 py-2 rounded-lg hover:bg-rose-50 transition-colors ml-1"
                >
                  <X size={13} /> Limpiar todo
                </button>
              )}
            </div>
          </div>

          {/* Active filter chips */}
          {(selected.cities.length || selected.countries.length || selected.languages.length || selected.specialties.length || selected.modalities.length || letter !== 'TODOS') ? (
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {letter !== 'TODOS' && (
                <FilterChip label={`Letra: ${letter}`} onRemove={() => setLetter('TODOS')} />
              )}
              {selected.countries.map((v) => <FilterChip key={`co-${v}`} label={`País: ${v}`} onRemove={() => toggleFacet('countries', v)} />)}
              {selected.cities.map((v) => <FilterChip key={`ci-${v}`} label={`Ciudad: ${v}`} onRemove={() => toggleFacet('cities', v)} />)}
              {selected.languages.map((v) => <FilterChip key={`la-${v}`} label={`Idioma: ${v}`} onRemove={() => toggleFacet('languages', v)} />)}
              {selected.specialties.map((v) => <FilterChip key={`sp-${v}`} label={`Especialidad: ${v}`} onRemove={() => toggleFacet('specialties', v)} />)}
              {selected.modalities.map((v) => <FilterChip key={`mo-${v}`} label={`Modalidad: ${v}`} onRemove={() => toggleFacet('modalities', v)} />)}
            </div>
          ) : null}

          {/* Main layout: A-Z rail + filters sidebar + results */}
          <div className="flex gap-6 items-start">
            {/* A-Z sticky rail */}
            <aside className="hidden md:block sticky top-24 self-start flex-shrink-0">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-2 flex flex-col gap-0.5">
                <button
                  onClick={() => setLetter('TODOS')}
                  data-testid="directory-letter-TODOS"
                  className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1.5 rounded-md transition-colors ${
                    letter === 'TODOS'
                      ? 'bg-cmic-500 text-white'
                      : 'text-charcoal-600 hover:bg-cmic-50 hover:text-cmic-700'
                  }`}
                >
                  TODO
                </button>
                {ALPHABET.map((L) => {
                  const has = availableLetters.has(L);
                  const isActive = letter === L;
                  return (
                    <button
                      key={L}
                      disabled={!has && !isActive}
                      onClick={() => setLetter((current) => current === L ? 'TODOS' : L)}
                      data-testid={`directory-letter-${L}`}
                      className={`text-xs font-bold w-7 h-7 rounded-md transition-colors ${
                        isActive
                          ? 'bg-cmic-500 text-white shadow-sm'
                          : has
                            ? 'text-charcoal-700 hover:bg-cmic-50 hover:text-cmic-700'
                            : 'text-charcoal-300 cursor-not-allowed'
                      }`}
                    >
                      {L}
                    </button>
                  );
                })}
              </div>
            </aside>

            {/* Filters now live in the top horizontal dropdown bar (desktop) and the mobile drawer */}

            {/* Results */}
            <main className="flex-1 min-w-0">
              {loading ? (
                <div className="text-center py-16" data-testid="directory-loading">
                  <div className="w-10 h-10 border-3 border-cmic-200 border-t-cmic-500 rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-charcoal-500">{t.directory.loading}</p>
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border border-gray-200" data-testid="directory-empty">
                  <Users size={48} className="text-charcoal-300 mx-auto mb-4" />
                  <p className="text-charcoal-700 text-lg mb-2 font-semibold">Sin coincidencias</p>
                  <p className="text-charcoal-500 text-sm mb-4">No encontramos intérpretes con esos filtros.</p>
                  <button
                    onClick={clearAll}
                    className="inline-flex items-center gap-2 bg-cmic-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-cmic-600"
                  >
                    Limpiar filtros
                  </button>
                </div>
              ) : (
                <ResultsByLetter
                  grouped={groupedByLetter}
                  profileBase={profileBase}
                  letterFilter={letter}
                  query={search}
                />
              )}
            </main>
          </div>
        </div>
      </section>

      {/* Mobile-only drawer (desktop uses the horizontal dropdown bar) */}
      <div className="md:hidden">
        <FiltersDrawer
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          facets={facets}
          selected={selected}
          toggleFacet={toggleFacet}
          clearAll={() => setSelected({ cities: [], countries: [], languages: [], specialties: [], modalities: [] })}
          activeFilterCount={selected.cities.length + selected.countries.length + selected.languages.length + selected.specialties.length + selected.modalities.length}
          resultCount={filtered.length}
        />
      </div>
    </>
  );
}


// ── Horizontal multi-select dropdown (desktop top bar) ─────────────────────
function DirectoryFilterDropdown({ label, icon: Icon, items, selected, onToggle, onClear, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const ql = q.trim().toLowerCase();
  const shown = ql ? items.filter(([n]) => n.toLowerCase().includes(ql)) : items;
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testid}-trigger`}
        className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium border transition-colors ${selected.length ? 'bg-cmic-50 text-cmic-700 border-cmic-300' : 'bg-white text-charcoal-700 border-gray-200 hover:border-cmic-300'}`}
      >
        <Icon size={14} className={selected.length ? 'text-cmic-500' : 'text-charcoal-400'} /> {label}
        {selected.length > 0 && (
          <span className="bg-cmic-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{selected.length}</span>
        )}
        <ChevronDown size={14} className={`text-charcoal-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div data-testid={`${testid}-popover`} className="absolute z-30 mt-2 left-0 w-64 bg-white border border-gray-200 rounded-xl shadow-xl p-3">
          {items.length > 6 && (
            <div className="relative mb-2">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-charcoal-400" />
              <input
                type="text"
                autoFocus
                placeholder={`Buscar ${label.toLowerCase()}…`}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-cmic-300"
              />
            </div>
          )}
          <ul className="space-y-0.5 max-h-64 overflow-y-auto">
            {shown.map(([name, count]) => {
              const checked = selected.includes(name);
              return (
                <li key={name}>
                  <label className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-cmic-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggle(name)}
                      data-testid={`${testid}-option-${name.replace(/\W+/g, '-').toLowerCase()}`}
                      className="rounded text-cmic-500 focus:ring-cmic-500"
                    />
                    <span className={`flex-1 text-sm ${checked ? 'font-semibold text-cmic-700' : 'text-charcoal-700'}`}>{name}</span>
                    <span className="text-[11px] text-charcoal-400 font-mono">{count}</span>
                  </label>
                </li>
              );
            })}
            {shown.length === 0 && (
              <li className="text-xs text-charcoal-400 py-2 text-center">Sin coincidencias</li>
            )}
          </ul>
          {selected.length > 0 && (
            <button
              onClick={onClear}
              data-testid={`${testid}-clear`}
              className="w-full mt-2 pt-2 border-t border-gray-100 text-[11px] font-medium text-charcoal-500 hover:text-rose-600 flex items-center justify-center gap-1"
            >
              <X size={11} /> Limpiar {label.toLowerCase()}
            </button>
          )}
        </div>
      )}
    </div>
  );
}


// ── Filter chip used at top ────────────────────────────────────────────────
function FilterChip({ label, onRemove }) {
  const slug = label.replace(/\W+/g, '-').toLowerCase();
  return (
    <span data-testid={`directory-active-chip-${slug}`} className="inline-flex items-center gap-1.5 bg-cmic-50 text-cmic-700 border border-cmic-200 px-3 py-1.5 rounded-full text-xs font-medium">
      {label}
      <button onClick={onRemove} data-testid={`directory-active-chip-remove-${slug}`} className="hover:bg-cmic-100 rounded-full p-0.5">
        <X size={11} />
      </button>
    </span>
  );
}


// ── Results grouped by initial letter ──────────────────────────────────────
function ResultsByLetter({ grouped, profileBase, letterFilter, query }) {
  // If user picked a specific letter, just show that group (no separator).
  // If TODOS, render multiple groups with sticky letter headings.
  const letters = Array.from(grouped.keys()).sort((a, b) => {
    if (a === '#') return 1;
    if (b === '#') return -1;
    return a.localeCompare(b);
  });
  return (
    <div className="space-y-8">
      {letters.map((L) => (
        <section key={L} data-testid={`directory-section-${L}`}>
          {letterFilter === 'TODOS' && (
            <div id={`letter-${L}`} className="sticky top-20 z-10 bg-gray-50/95 backdrop-blur py-2 mb-3 -mx-2 px-2">
              <h2 className="text-cmic-600 font-bold text-2xl flex items-center gap-3">
                <span className="bg-cmic-500 text-white w-9 h-9 rounded-lg flex items-center justify-center text-base shadow-sm">{L}</span>
                <span className="text-charcoal-400 text-xs font-medium">{grouped.get(L).length} intérpretes</span>
              </h2>
            </div>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-fr">
            {grouped.get(L).map((it) => <Card key={it.id || it.slug} it={it} profileBase={profileBase} query={query} />)}
          </div>
        </section>
      ))}
    </div>
  );
}

function Card({ it, profileBase, query }) {
  const displayedName = displayName(it);
  const initials = (it.nombres || it.full_name || '').split(' ').slice(0, 2).map((n) => n.charAt(0)).join('');
  const allLangs = Array.from(new Set([
    ...(it.active_languages || []),
    ...(it.passive_languages || []),
  ])).filter(Boolean);
  const langsFallback = !allLangs.length && it.languages
    ? (Array.isArray(it.languages)
        ? it.languages
        : String(it.languages).split(/[,/·]+/)
      ).map((s) => String(s).trim()).filter(Boolean)
    : [];
  const displayLangs = allLangs.length ? allLangs : langsFallback;
  const MAX_CHIPS = 3;
  const visibleLangs = displayLangs.slice(0, MAX_CHIPS);
  const extra = Math.max(0, displayLangs.length - MAX_CHIPS);
  // Modality chips (compact icon + short label). Show up to 3, then "+N".
  const allMods = Array.isArray(it.modalities) ? it.modalities.filter(Boolean) : [];
  const MAX_MOD_CHIPS = 3;
  const visibleMods = allMods.slice(0, MAX_MOD_CHIPS);
  const extraMods = Math.max(0, allMods.length - MAX_MOD_CHIPS);
  const isCandidate = it.membership_status === 'candidate';
  return (
    <Link
      to={`${profileBase}/${it.slug}`}
      data-testid={`directory-card-${it.slug}`}
      className="group relative bg-white rounded-xl p-5 shadow-sm hover:shadow-lg transition-all duration-200 border border-gray-100 hover:border-cmic-300 hover:-translate-y-0.5 flex items-center gap-4 min-h-[7rem]"
    >
      {isCandidate && (
        <span
          title="Candidato a miembro"
          data-testid="directory-candidate-badge"
          className="absolute top-2 right-2 inline-flex items-center text-[9px] font-semibold tracking-wider uppercase text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
        >
          Candidato
        </span>
      )}
      {it.photo_url ? (
        <img src={absoluteUrl(it.photo_url)} alt={displayedName} className="w-14 h-14 rounded-full object-cover object-top ring-2 ring-cmic-50 flex-shrink-0" />
      ) : (
        <div className="w-14 h-14 bg-gradient-to-br from-cmic-500 to-cmic-700 rounded-full flex items-center justify-center flex-shrink-0 ring-2 ring-cmic-100">
          <span className="text-white font-bold text-base">{initials}</span>
        </div>
      )}
      <div className="min-w-0 flex-1">
        <h3 className="font-semibold text-charcoal-800 group-hover:text-cmic-600 transition-colors leading-tight text-[15px] line-clamp-1" title={displayedName}>
          <Highlight text={displayedName} query={query} />
        </h3>
        {it.city && (
          <div className="inline-flex items-center gap-1 text-[11px] text-charcoal-500 mt-0.5">
            <MapPin size={10} className="text-cmic-500 flex-shrink-0" />
            <span className="truncate"><Highlight text={it.city} query={query} /></span>
          </div>
        )}
        {visibleLangs.length > 0 && (
          <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid={`directory-card-${it.slug}-langs`}>
            <Languages size={11} className="text-cmic-400 flex-shrink-0" />
            {visibleLangs.map((lng) => (
              <span key={lng} title={lng} className="inline-flex items-center max-w-[7.5rem] truncate text-[11px] font-medium bg-cmic-50 text-cmic-700 px-1.5 py-0.5 rounded">
                <Highlight text={lng} query={query} />
              </span>
            ))}
            {extra > 0 && (
              <span className="text-[11px] font-medium text-charcoal-500">+{extra}</span>
            )}
          </div>
        )}
        {visibleMods.length > 0 && (
          <div className="mt-1.5 flex items-center gap-1 flex-wrap" data-testid={`directory-card-${it.slug}-mods`}>
            {visibleMods.map((m) => {
              const meta = modalityChipMeta(m);
              const Icon = meta.icon;
              return (
                <span
                  key={m}
                  title={meta.full}
                  className="inline-flex items-center gap-1 max-w-[8rem] truncate text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-100 px-1.5 py-0.5 rounded-full"
                >
                  <Icon size={10} className="flex-shrink-0 text-violet-500" />
                  <span className="truncate">{meta.short}</span>
                </span>
              );
            })}
            {extraMods > 0 && (
              <span className="text-[10px] font-medium text-violet-600">+{extraMods}</span>
            )}
          </div>
        )}
      </div>
      <ChevronRight size={16} className="text-charcoal-300 group-hover:text-cmic-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
    </Link>
  );
}


// ── Lateral filters drawer (department-store style) ────────────────────────
function FiltersDrawer({ open, onClose, facets, selected, toggleFacet, clearAll, activeFilterCount, resultCount }) {
  // Local search inside each facet group for usability with long lists.
  const [search, setSearch] = useState({ cities: '', countries: '', languages: '', specialties: '', modalities: '' });
  // Open/close per group
  const [openGroups, setOpenGroups] = useState({ countries: true, cities: true, languages: true, modalities: true, specialties: false });
  function toggleGroup(g) { setOpenGroups((s) => ({ ...s, [g]: !s[g] })); }

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50" data-testid="directory-filters-drawer">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-fadeIn" onClick={onClose} />
      <aside className="absolute top-0 right-0 bottom-0 w-full sm:w-[420px] bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-cmic-50 to-white">
          <h2 className="font-bold text-charcoal-800 flex items-center gap-2">
            <SlidersHorizontal size={18} className="text-cmic-500" /> Filtros
            {activeFilterCount > 0 && (
              <span className="bg-cmic-500 text-white text-[11px] font-bold rounded-full min-w-[20px] h-[20px] flex items-center justify-center px-1.5">
                {activeFilterCount}
              </span>
            )}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-charcoal-500 hover:bg-gray-100" data-testid="directory-close-filters-btn">
            <X size={18} />
          </button>
        </div>

        {/* Groups */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          <FilterGroup
            id="countries"
            label="País"
            icon={MapPinned}
            items={facets.countries}
            selected={selected.countries}
            onToggle={(v) => toggleFacet('countries', v)}
            open={openGroups.countries}
            onToggleOpen={() => toggleGroup('countries')}
            search={search.countries}
            setSearch={(v) => setSearch((s) => ({ ...s, countries: v }))}
          />
          <FilterGroup
            id="cities"
            label="Ciudad"
            icon={MapPin}
            items={facets.cities}
            selected={selected.cities}
            onToggle={(v) => toggleFacet('cities', v)}
            open={openGroups.cities}
            onToggleOpen={() => toggleGroup('cities')}
            search={search.cities}
            setSearch={(v) => setSearch((s) => ({ ...s, cities: v }))}
          />
          <FilterGroup
            id="languages"
            label="Idioma"
            icon={Languages}
            items={facets.languages}
            selected={selected.languages}
            onToggle={(v) => toggleFacet('languages', v)}
            open={openGroups.languages}
            onToggleOpen={() => toggleGroup('languages')}
            search={search.languages}
            setSearch={(v) => setSearch((s) => ({ ...s, languages: v }))}
          />
          <FilterGroup
            id="modalities"
            label="Modalidad"
            icon={Mic}
            items={facets.modalities}
            selected={selected.modalities}
            onToggle={(v) => toggleFacet('modalities', v)}
            open={openGroups.modalities}
            onToggleOpen={() => toggleGroup('modalities')}
            search={search.modalities}
            setSearch={(v) => setSearch((s) => ({ ...s, modalities: v }))}
          />
          <FilterGroup
            id="specialties"
            label="Especialidad"
            icon={Briefcase}
            items={facets.specialties}
            selected={selected.specialties}
            onToggle={(v) => toggleFacet('specialties', v)}
            open={openGroups.specialties}
            onToggleOpen={() => toggleGroup('specialties')}
            search={search.specialties}
            setSearch={(v) => setSearch((s) => ({ ...s, specialties: v }))}
          />
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex items-center gap-2">
          <button
            onClick={clearAll}
            data-testid="directory-clear-drawer-btn"
            className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium text-charcoal-600 hover:bg-white border border-gray-200"
          >
            Limpiar
          </button>
          <button
            onClick={onClose}
            data-testid="directory-apply-filters-btn"
            className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold bg-cmic-500 text-white hover:bg-cmic-600"
          >
            Ver {resultCount} resultados
          </button>
        </div>
      </aside>
    </div>
  );
}


function FilterGroup({ id, label, icon: Icon, items, selected, onToggle, open, onToggleOpen, search, setSearch }) {
  const q = search.trim().toLowerCase();
  const shown = q
    ? items.filter(([name]) => name.toLowerCase().includes(q))
    : items;
  return (
    <div className="border-b border-gray-100">
      <button
        onClick={onToggleOpen}
        data-testid={`directory-filter-group-${id}`}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50"
      >
        <span className="flex items-center gap-2 text-sm font-bold text-charcoal-800">
          <Icon size={16} className="text-cmic-500" /> {label}
          {selected.length > 0 && (
            <span className="bg-cmic-100 text-cmic-700 text-[10px] font-bold rounded px-1.5 py-0.5 ml-1">
              {selected.length}
            </span>
          )}
        </span>
        <ChevronDown size={16} className={`text-charcoal-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-4">
          {items.length > 6 && (
            <div className="relative mb-2">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-charcoal-400" />
              <input
                type="text"
                placeholder={`Buscar ${label.toLowerCase()}…`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-cmic-300"
              />
            </div>
          )}
          <ul className="space-y-0.5 max-h-72 overflow-y-auto">
            {shown.map(([name, count]) => {
              const checked = selected.includes(name);
              return (
                <li key={name}>
                  <label className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-cmic-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggle(name)}
                      data-testid={`directory-filter-${id}-${name}`}
                      className="rounded text-cmic-500 focus:ring-cmic-500"
                    />
                    <span className={`flex-1 text-sm ${checked ? 'font-semibold text-cmic-700' : 'text-charcoal-700'}`}>{name}</span>
                    <span className="text-[11px] text-charcoal-400 font-mono">{count}</span>
                  </label>
                </li>
              );
            })}
            {shown.length === 0 && (
              <li className="text-xs text-charcoal-400 py-2 text-center">Sin coincidencias</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
